import {useQuery} from '@tanstack/react-query'
import {createContext, memo, useCallback, useContext, useMemo, useState} from 'react'

import {apiCountMatchedRepositoryItems} from '../../api/repository/api-count-matched-repository-items'
import type {CountMatchedRepositoryItemsRequest} from '../../api/repository/contracts'
import {useProjectNumber} from '../memex/use-project-number'

export type AutoAddItemCountContextType = {
  /**
   * The count of items that would be auto-added from the attached repository
   */
  autoAddItemCount: number

  /**
   * Function to refresh the auto-add item count for a specific repository and query
   */
  refreshAutoAddItemCount: (repositoryId: number, query: string) => Promise<void>
}

const AutoAddItemCountContext = createContext<AutoAddItemCountContextType | null>(null)

export const AutoAddItemCountProvider = memo(function AutoAddItemCountProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const {projectNumber} = useProjectNumber()
  const [queryParams, setQueryParams] = useState<CountMatchedRepositoryItemsRequest | null>(null)

  const {data, refetch, isError} = useQuery({
    queryKey: ['autoAddItemCount', queryParams],
    queryFn: () => (queryParams ? apiCountMatchedRepositoryItems(queryParams) : Promise.resolve(null)),
    enabled: queryParams !== null,
    retry: false,
  })

  const autoAddItemCount = isError ? 0 : data?.count ?? 0

  const refreshAutoAddItemCount = useCallback(
    async (repositoryId: number, query: string) => {
      const params: CountMatchedRepositoryItemsRequest = {
        repositoryId,
        query,
        memexNumber: projectNumber,
      }
      setQueryParams(params)
      await refetch()
    },
    [projectNumber, refetch],
  )

  const contextValue = useMemo(
    () => ({
      autoAddItemCount,
      refreshAutoAddItemCount,
    }),
    [autoAddItemCount, refreshAutoAddItemCount],
  )

  return <AutoAddItemCountContext.Provider value={contextValue}>{children}</AutoAddItemCountContext.Provider>
})

export const useAutoAddItemCount = (): AutoAddItemCountContextType => {
  const contextValue = useContext(AutoAddItemCountContext)

  if (!contextValue) {
    throw new Error('useAutoAddItemCount must be used within an AutoAddItemCountProvider')
  }

  return contextValue
}

try{ AutoAddItemCountContext.displayName ||= 'AutoAddItemCountContext' } catch {}
try{ AutoAddItemCountProvider.displayName ||= 'AutoAddItemCountProvider' } catch {}